import {
  useContext,
  useId,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import { useRouter } from "next/compat/router"

import {
  Alert,
  AlertDescription,
  Button,
  cn,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableColumnToggle,
  TableControlsRow,
  type TableProps,
} from "@coval/ui"

import { DisplayMenu } from "@/components/filters/DisplayMenu"
import { TableCsvDownloadButton } from "@/components/table-actions/TableCsvDownloadButton"
import { TableDisplayContext } from "@/contexts/TableDisplayContext"
import { downloadCSV } from "@/utils/csv/csv-export"
import { buildBrowseTableCsv } from "@/utils/csv/tableCsvColumns"

/** The CSV-export error a browse table shows above its rows, with a retry. */
export function BrowseTableCsvErrorAlert({
  error,
}: {
  error: { message: string; onRetry: () => void }
}) {
  return (
    <Alert variant="danger">
      <AlertDescription>
        {error.message}
        <Button type="button" variant="ghost" onClick={error.onRetry}>
          Retry
        </Button>
      </AlertDescription>
    </Alert>
  )
}

interface TableGroupOption<T> {
  id: string
  label: string
  value: (row: T) => string | null | undefined
  format?: (value: string) => string
}

type BrowseTableProps<T extends object> = TableProps<T> & {
  groupOptions?: TableGroupOption<T>[]
  displayControls?: boolean
  groupingControls?: ReactNode
  columnControls?: ReactNode
  csvControl?: ReactNode
  csvDisabled?: boolean
  csvError?: { message: string; onRetry: () => void }
  csvScope?: string
  csvFilters?: string[]
  exportRowId?: (row: T) => string
  isExportRow?: (row: T) => boolean
}

/** App table composition; the design-system table owns rendering and interaction. */
export function BrowseTable<T extends object>(props: BrowseTableProps<T>) {
  const router = useRouter()
  return <BrowseTableView key={router?.asPath.split("?")[0] ?? ""} {...props} />
}

function BrowseTableView<T extends object>({
  groupOptions = [],
  displayControls = true,
  columnControls,
  groupingControls,
  csvControl,
  csvDisabled,
  csvError,
  csvScope,
  csvFilters,
  isExportRow,
  exportRowId,
  ...props
}: BrowseTableProps<T>) {
  const displayTarget = useContext(TableDisplayContext)
  const [groupBy, setGroupBy] = useState<string | null>(null)
  const groupBySelectId = useId()
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [localVisibility, setLocalVisibility] = useState<
    Record<string, boolean>
  >({})
  const visibility = props.columnVisibility ?? localVisibility
  const grouping =
    !props.getSubRows && groupOptions.find((option) => option.id === groupBy)
  const { rows, groups } = useMemo(() => {
    const groups = new Map<T, { id: string; label: string; children: T[] }>()
    if (!grouping) {
      return { rows: props.data, groups }
    }
    const buckets = new Map<string | null, T[]>()
    for (const row of props.data) {
      const value = grouping.value(row) ?? null
      const bucket = buckets.get(value) ?? []
      bucket.push(row)
      buckets.set(value, bucket)
    }
    const occupiedIds = new Set(props.data.map(props.getRowId))
    const rows = Array.from(buckets, ([value, children]) => {
      // A separate object represents the section. Resource callbacks never see it.
      const group = { ...children[0] }
      let id = JSON.stringify(["table-group", grouping.id, value])
      while (occupiedIds.has(id)) {
        id = `:${id}`
      }
      occupiedIds.add(id)
      groups.set(group, {
        id,
        label:
          value === null || value === ""
            ? `No ${grouping.label.toLowerCase()}`
            : (grouping.format?.(value) ?? value),
        children,
      })
      return group
    })
    return { rows, groups }
  }, [grouping, props.data, props.getRowId])
  const setVisibility = (next: Record<string, boolean>) => {
    setLocalVisibility(next)
    props.onColumnVisibilityChange?.(next)
  }
  const groupIds = Array.from(groups.values(), (group) => group.id)
  const isGroup = (row: T) => groups.has(row)
  const groupingProps: Partial<TableProps<T>> = grouping
    ? {
        data: rows,
        getRowId: (row) => groups.get(row)?.id ?? props.getRowId(row),
        getSubRows: (row) => groups.get(row)?.children,
        renderGroupRow: (row) => {
          const group = groups.get(row)
          return group ? (
            <span className="flex items-center gap-2">
              <span className="font-medium">{group.label}</span>
              <span className="text-xs tabular-nums text-coval-text-muted">
                {group.children.length}
              </span>
            </span>
          ) : undefined
        },
        expandedIds: groupIds.filter((id) => !collapsed.has(id)),
        onExpandedChange: (expanded) =>
          setCollapsed(
            new Set(groupIds.filter((id) => !expanded.includes(id))),
          ),
        onRowClick: (row, event) => {
          if (!isGroup(row)) {
            props.onRowClick?.(row, event)
          }
        },
        isRowClickable: (row) =>
          !isGroup(row) &&
          (props.isRowClickable?.(row) ?? Boolean(props.onRowClick)),
        isRowUnread: props.isRowUnread
          ? (row) => !isGroup(row) && props.isRowUnread!(row)
          : undefined,
        rowVariant: props.rowVariant
          ? (row) => (isGroup(row) ? undefined : props.rowVariant!(row))
          : undefined,
        rowActions: props.rowActions
          ? (row) => (isGroup(row) ? [] : props.rowActions!(row))
          : undefined,
        rowHref: props.rowHref
          ? (row) => (isGroup(row) ? undefined : props.rowHref!(row))
          : undefined,
        rowCopyId: props.rowCopyId
          ? (row) =>
              isGroup(row)
                ? undefined
                : typeof props.rowCopyId === "function"
                  ? props.rowCopyId(row)
                  : props.getRowId(row)
          : undefined,
        rowAttributes: props.rowAttributes
          ? (row) => (isGroup(row) ? {} : props.rowAttributes!(row))
          : undefined,
        ...(props.selectable
          ? {
              isRowSelectable: (row: T) =>
                !isGroup(row) && (props.isRowSelectable?.(row) ?? true),
              isRowSelectionDisabled: (row: T) =>
                !isGroup(row) && (props.isRowSelectionDisabled?.(row) ?? false),
            }
          : {}),
      }
    : {}

  const tableProps = {
    ...props,
    ...groupingProps,
    columnVisibility: visibility,
    onColumnVisibilityChange: setVisibility,
    className: cn(props.className, "min-h-0 flex-1"),
  } as TableProps<T>

  const displayMenu = (
    <DisplayMenu summary={grouping ? grouping.label : undefined}>
      {grouping && (
        <span className="block text-xs text-coval-text-muted">
          {groupIds.length} {groupIds.length === 1 ? "group" : "groups"} ·{" "}
          {props.data.length} {props.onLoadMore ? "loaded " : ""}
          {props.data.length === 1 ? "row" : "rows"}
        </span>
      )}

      {groupingControls ??
        (groupOptions.length > 0 && !props.getSubRows && (
          <div className="space-y-2">
            <Label htmlFor={groupBySelectId}>Group by</Label>
            <Select
              value={groupBy === null ? "none" : `field:${groupBy}`}
              onValueChange={(value) => {
                setGroupBy(value === "none" ? null : value.slice(6))
                setCollapsed(new Set())
              }}
            >
              <SelectTrigger id={groupBySelectId} aria-label="Group by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No grouping</SelectItem>
                {groupOptions.map((option) => (
                  <SelectItem key={option.id} value={`field:${option.id}`}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {grouping && (
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setCollapsed(collapsed.size ? new Set() : new Set(groupIds))
                }
              >
                {collapsed.size ? "Expand all groups" : "Collapse all groups"}
              </Button>
            )}
            {grouping && props.onLoadMore && (
              <p className="text-xs text-coval-text-muted">
                Groups include loaded rows. More rows join their groups as you
                scroll.
              </p>
            )}
          </div>
        ))}
      {columnControls ?? (
        <TableColumnToggle
          inline
          columns={props.columns}
          columnVisibility={visibility}
          onColumnVisibilityChange={setVisibility}
        />
      )}
    </DisplayMenu>
  )

  const csv = buildBrowseTableCsv({
    rows: props.data,
    columns: props.columns,
    visibility,
    columnOrder: props.columnOrder,
    getSubRows: props.getSubRows,
    isExportRow,
    getRowId: exportRowId ?? props.getRowId,
  })
  const controls = (
    <>
      {displayMenu}
      {csvControl ?? (
        <TableCsvDownloadButton
          rowCount={csv.rowCount}
          columns={csv.columns}
          filters={csvFilters}
          scope={csvScope}
          disabled={props.isLoading || csvDisabled || Boolean(csvError)}
          onClick={() => {
            const name = (props["aria-label"] ?? "table")
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
            downloadCSV(
              csv.createCsv(),
              `${name}-${new Date().toISOString().slice(0, 10)}.csv`,
            )
          }}
        />
      )}
    </>
  )

  if (!displayControls) {
    return <Table {...props} />
  }
  return (
    <div
      className="flex h-full min-h-0 min-w-0 flex-col"
      // An `edgeToEdge` table pads nothing of its own because the container
      // around this whole composition already does. The controls row sits
      // inside that same container, so it resolves the same zero gutter rather
      // than `table-pad`'s standalone 12px, and the toolbar stays on the first
      // column's line. A composition that pads the table alone puts its own
      // `--table-edge-gutter` on the scope the controls portal into, which this
      // never reaches.
      style={
        props.edgeToEdge
          ? ({ "--table-edge-gutter": "0px" } as CSSProperties)
          : undefined
      }
    >
      {displayTarget ? (
        displayTarget.target ? (
          createPortal(controls, displayTarget.target)
        ) : null
      ) : (
        <TableControlsRow>{controls}</TableControlsRow>
      )}
      {csvError && <BrowseTableCsvErrorAlert error={csvError} />}
      <Table {...tableProps} />
    </div>
  )
}
